import {
  Choice,
  CustomRequestOption,
  isFile,
  Segment,
  showNotification,
  TextInput,
  Upload,
  UploadState,
} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useUploadFileMutation} from '@dms/api/base';
import {
  CompositionFont,
  CompositionFontStyle,
  useCreateCompositionFontMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {getFontFamilies} from '../utils/getFontFamilies';
import {getFontFamilyFromFileName} from '../utils/getFontFamilyFromFileName';
import {BOLD_FONT_WEIGHT, FontFacePatch, REGULAR_FONT_WEIGHT} from '../utils/toFontFacePatch';

interface FontUploadProps extends TestIdProps {
  fonts: CompositionFont[];
  onUploaded: (patch: FontFacePatch) => void;
}

const FONT_FILE_ACCEPT = '.ttf,.otf,font/ttf,font/otf';
const NEW_FAMILY_OPTION_VALUE = 'new-family';
const FAMILY_NAME_MAX_LENGTH = 255;

// Uploaded faces are offered only in the weights a text layer can reference.
type UploadFontWeight = `${FontFacePatch['fontWeight']}`;

const WEIGHT_OPTIONS: {value: UploadFontWeight; label: string}[] = [
  {value: '400', label: i18n.t('entity.watermark.composition.labels.regular')},
  {value: '700', label: i18n.t('entity.watermark.composition.labels.bold')},
];

const STYLE_OPTIONS: {value: CompositionFontStyle; label: string}[] = [
  {value: 'normal', label: i18n.t('entity.watermark.composition.labels.normal')},
  {value: 'italic', label: i18n.t('entity.watermark.composition.labels.italic')},
];

/**
 * Uploads one font face. Weight and style belong to the face, so they are chosen here and not on
 * the text layer. The face joins an existing family or starts a new one (named from the file when
 * no name is entered).
 */
export function FontUpload(props: FontUploadProps) {
  const [uploadFile] = useUploadFileMutation();
  const [createCompositionFont, {isLoading: isCreatingFont}] = useCreateCompositionFontMutation();

  // Family name, not a face id: the family outlives any of its faces. Null starts a new family.
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [uploadWeight, setUploadWeight] = useState<UploadFontWeight>('400');
  const [uploadStyle, setUploadStyle] = useState<CompositionFontStyle>('normal');

  const fontFamilies = getFontFamilies(props.fonts);
  const existingFamily = fontFamilies.find((family) => family.family === selectedFamily);
  const familyOptions = [
    {
      value: NEW_FAMILY_OPTION_VALUE,
      label: i18n.t('entity.watermark.composition.labels.newFontFamily'),
    },
    ...fontFamilies.map((family) => ({
      value: family.optionValue,
      label: family.family,
    })),
  ];

  const handleUpload = (option: CustomRequestOption) => {
    if (!isFile(option.file)) {
      return;
    }
    const file = option.file;

    const weight = uploadWeight === '700' ? BOLD_FONT_WEIGHT : REGULAR_FONT_WEIGHT;
    const trimmedFamilyName = newFamilyName.trim();
    const family =
      existingFamily?.family ??
      (trimmedFamilyName.length > 0 ? trimmedFamilyName : getFontFamilyFromFileName(file.name));

    uploadFile({file})
      .unwrap()
      .then((uploaded) =>
        createCompositionFont({
          createCompositionFontRequestBody: {
            family,
            weight,
            style: uploadStyle,
            fileId: uploaded.fileId,
          },
        }).unwrap()
      )
      .then((font) => {
        // The next face of a new family most likely belongs to the same family.
        if (isNil(existingFamily)) {
          setSelectedFamily(family);
          setNewFamilyName('');
        }
        props.onUploaded({fontId: font.id, fontWeight: weight});
        showNotification.success(i18n.t('entity.watermark.composition.notifications.fontUploaded'));
        option.onSuccess?.({});
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        option.onError?.(new Error('Font upload failed'));
      });
  };

  return (
    <VStack spacing={2}>
      <Choice<string>
        label={i18n.t('entity.watermark.composition.labels.fontFamily')}
        value={existingFamily?.optionValue ?? NEW_FAMILY_OPTION_VALUE}
        options={familyOptions}
        isNotClearable
        onChange={(value) =>
          setSelectedFamily(props.fonts.find((font) => font.id === value)?.family ?? null)
        }
        data-testid={suffixTestId('uploadFamily', props)}
      />
      <Show when={isNil(existingFamily)}>
        <TextInput
          label={i18n.t('entity.watermark.composition.labels.newFontFamilyName')}
          value={newFamilyName}
          maxLength={FAMILY_NAME_MAX_LENGTH}
          onChange={(value) => setNewFamilyName(value ?? '')}
          data-testid={suffixTestId('uploadFamilyName', props)}
        />
      </Show>
      <HStack spacing={2} align="flex-end" wrap>
        <Segment<UploadFontWeight>
          value={uploadWeight}
          options={WEIGHT_OPTIONS}
          onChange={setUploadWeight}
          data-testid={suffixTestId('uploadWeight', props)}
        />
        <Segment<CompositionFontStyle>
          value={uploadStyle}
          options={STYLE_OPTIONS}
          onChange={setUploadStyle}
          data-testid={suffixTestId('uploadStyle', props)}
        />
        <Upload
          type="button"
          accept={FONT_FILE_ACCEPT}
          buttonVariant="secondary"
          uploadIcon="content/font_download"
          uploadText={i18n.t('entity.watermark.composition.actions.uploadFont')}
          uploadingText={i18n.t('general.labels.uploadingWithDots')}
          uploadState={isCreatingFont ? UploadState.Uploading : UploadState.Idle}
          customRequest={handleUpload}
          data-testid={suffixTestId('uploadFont', props)}
        />
      </HStack>
    </VStack>
  );
}
